// New Idea wizard - the guided capture flow. The launch button posts to the
// engine endpoint, which goes live with the engine milestone; until then a
// failed launch reports that honestly instead of pretending.

import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useEngineAuth, useRuns } from "../api";
import { fmtSize, UPLOAD_ACCEPT, uploadBatch } from "../components/AttachFiles";
import { statusLabel } from "../components/bits";

type ModeHint = "problem" | "solution" | "discover" | null;
type Depth = "quick" | "standard" | "deep";

const DEPTHS: { key: Depth; title: string; copy: string }[] = [
  {
    key: "quick",
    title: "Quick scan",
    copy: "2 research tracks, one Skeptic pass, no revision round. Answers: is this worth a full run?",
  },
  {
    key: "standard",
    title: "Standard (recommended)",
    copy: "4 research tracks, 1 revision round, the Skeptic checks both the evidence and the final plan. For any idea you might actually build.",
  },
  {
    key: "deep",
    title: "Deep dive",
    copy: "6 tracks including economics and risk/regulatory, 2 revision rounds, extra red-team pass. For bet-the-quarter decisions.",
  },
];

const MODES: { key: Exclude<ModeHint, null>; label: string; desc: string }[] = [
  {
    key: "problem",
    label: "I have a problem",
    desc: "Describe a pain, cost, or gap. We'll hunt for solutions and pressure-test the best one.",
  },
  {
    key: "solution",
    label: "I have a solution to test",
    desc: "Pitch your idea. We'll pull out every claim baked into it and try to break each one.",
  },
  {
    key: "discover",
    label: "Find me an idea",
    desc: "Tell us what you're good at and what you can reach. A scout researches live trends and brings back 4-6 evidenced candidates; you pick one and the full run continues on it.",
  },
];

export function NewIdea() {
  const navigate = useNavigate();
  const runs = useRuns();
  const engineAuth = useEngineAuth();
  const authExpired = engineAuth.data?.state === "expired";
  const [modeHint, setModeHint] = useState<ModeHint>(null);
  const [ideaText, setIdeaText] = useState("");
  const [depth, setDepth] = useState<Depth>("standard");
  const [basisRunId, setBasisRunId] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [state, setState] = useState<
    "idle" | "uploading" | "launching" | "error"
  >("idle");
  const [message, setMessage] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);

  // Newest first, like the Ideas page: the run you just finished is the one
  // you are most likely to build round 2 on.
  const priorRuns = [...(runs.data ?? [])].sort((a, b) =>
    b.id.localeCompare(a.id),
  );
  const basis = priorRuns.find((r) => r.id === basisRunId) ?? null;
  const discover = modeHint === "discover";

  const addFiles = (incoming: FileList | File[]) => {
    const next = [...files];
    for (const f of Array.from(incoming)) {
      if (!next.some((x) => x.name === f.name && x.size === f.size)) {
        next.push(f);
      }
    }
    setFiles(next);
  };

  const launch = async () => {
    try {
      let uploadBatchId: string | null = null;
      if (files.length > 0) {
        setState("uploading");
        uploadBatchId = await uploadBatch(files);
      }
      setState("launching");
      const res = await fetch("/api/runs", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ideaText,
          modeHint,
          depth,
          basisRunId: basisRunId || null,
          uploadBatchId,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `${res.status} ${res.statusText}`);
      }
      // The run folder gets its name when Intake writes it; the card shows
      // up on Ideas via the event stream within a minute or two.
      navigate("/", {
        state: { justLaunched: true },
      });
    } catch (err) {
      setState("error");
      setMessage(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div className="breadcrumbs">
        <Link to="/">Ideas</Link> / New idea
      </div>
      <h1 style={{ fontSize: 21 }}>Start a run</h1>

      <div className="panel">
        <h2>How this works</h2>
        <p style={{ color: "var(--ink-2)" }}>
          <strong>Your idea gets stress-tested, not cheered on.</strong>{" "}
          Researchers gather evidence in parallel. An independent Skeptic then
          tries to break every claim - it reads only the files, never the chat,
          so it can't be charmed. You make the calls at three stop points; the
          machine never decides for you. Fair warning: the honest outcome is
          sometimes "kill this idea." That's the product working.
        </p>
      </div>

      <div className="panel">
        <h2>What are you bringing?</h2>
        <div className="gate-options">
          {MODES.map((m) => (
            <label
              key={m.key}
              className={`gate-option ${modeHint === m.key ? "selected" : ""}`}
            >
              <input
                type="radio"
                name="mode"
                checked={modeHint === m.key}
                onChange={() => setModeHint(m.key)}
              />
              <span>
                <div className="opt-label">{m.label}</div>
                <div className="opt-desc">{m.desc}</div>
              </span>
            </label>
          ))}
        </div>
        <p className="parse-warnings">
          {discover
            ? "The scout works from your profile: the sharper it is, the better the candidates fit you."
            : "Not sure? Pick the closest - the framing step sorts it out and you'll confirm before research starts."}
        </p>
        <textarea
          className="gate-form"
          style={{
            width: "100%",
            minHeight: 140,
            background: "var(--surface)",
            color: "var(--ink)",
            border: "1px solid var(--hairline)",
            borderRadius: 8,
            padding: 10,
            font: "inherit",
          }}
          placeholder={
            discover
              ? "What are you good at, and what do you know deeply? What can you reach (audiences, channels, networks)? Hours per week and capital you'd put in, and anything that's off the table."
              : "Describe it the way you'd tell a colleague. Rough is fine - the first step is turning this into a precise framing you'll confirm."
          }
          value={ideaText}
          onChange={(e) => setIdeaText(e.target.value)}
        />

        <div
          className="panel"
          style={{
            marginTop: 10,
            marginBottom: 0,
            padding: 10,
            border: `1px dashed var(${dragOver ? "--accent" : "--hairline"})`,
            background: "var(--surface)",
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            addFiles(e.dataTransfer.files);
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button
              className="btn secondary"
              type="button"
              onClick={() => fileInput.current?.click()}
            >
              Attach files
            </button>
            <span style={{ color: "var(--ink-2)", fontSize: 13 }}>
              Optional context: PDFs, docs, spreadsheets, images, or a zip -
              drop them here. They're committed into the run as its inputs.
            </span>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept={UPLOAD_ACCEPT}
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>
          {files.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                marginTop: 8,
              }}
            >
              {files.map((f) => (
                <span key={`${f.name}:${f.size}`} className="chip ghost">
                  {f.name} ({fmtSize(f.size)}){" "}
                  <button
                    type="button"
                    aria-label={`remove ${f.name}`}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--ink-2)",
                      padding: "0 2px",
                      font: "inherit",
                    }}
                    onClick={() =>
                      setFiles(files.filter((x) => x !== f))
                    }
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="panel">
        <h2>Build on a previous idea (optional)</h2>
        <p style={{ color: "var(--ink-2)", fontSize: 13.5, marginTop: 0 }}>
          Pick a previous run and this run starts from its findings: the
          verdict, the evidence that survived the Skeptic, refuted claims (so
          they stay dead), and any open questions or re-entry conditions. The
          previous run is read-only input - nothing in it is changed - and
          this run gets its own folder, so you can chain round 3, 4, 5 the
          same way by picking the newest round here.
        </p>
        <select
          value={basisRunId}
          onChange={(e) => setBasisRunId(e.target.value)}
          style={{
            width: "100%",
            background: "var(--surface)",
            color: "var(--ink)",
            border: "1px solid var(--hairline)",
            borderRadius: 8,
            padding: "9px 10px",
            font: "inherit",
          }}
        >
          <option value="">None - start fresh</option>
          {priorRuns.map((r) => (
            <option key={r.id} value={r.id}>
              {r.title} - {r.date} (
              {r.verdict?.split(/[\s(]/)[0] ?? statusLabel(r)})
            </option>
          ))}
        </select>
        {basis && (
          <p className="parse-warnings">
            Round 2+ of "{basis.title}": describe above what this round should
            do differently - what you learned, what to test next, or how the
            framing changes. Intake reads the prior findings alongside it.
          </p>
        )}
      </div>

      <div className="panel">
        <h2>Choose rigor</h2>
        <div className="gate-options">
          {DEPTHS.map((d) => (
            <label
              key={d.key}
              className={`gate-option ${depth === d.key ? "selected" : ""}`}
            >
              <input
                type="radio"
                name="depth"
                checked={depth === d.key}
                onChange={() => setDepth(d.key)}
              />
              <span>
                <div className="opt-label">{d.title}</div>
                <div className="opt-desc">{d.copy}</div>
              </span>
            </label>
          ))}
        </div>
        <p className="parse-warnings">
          {discover
            ? "The scout itself is a fixed, cheap step; the depth you pick applies to the full run after you choose a candidate."
            : "Intake will recommend a depth for this specific idea; you can change it at the first stop point."}
        </p>
      </div>

      {authExpired && (
        <div className="error-box" style={{ marginBottom: 10 }}>
          The server&apos;s Claude sign-in has expired, so runs cannot start.
          {engineAuth.data?.manage ? (
            <>
              {" "}
              Fix it in <Link to="/settings">Settings &gt; Engine authentication</Link>,
              then come back and launch.
            </>
          ) : (
            <> Ask the owner to re-authenticate the engine.</>
          )}
        </div>
      )}
      <button
        className="btn"
        disabled={
          authExpired ||
          ideaText.trim().length < 10 ||
          state === "uploading" ||
          state === "launching"
        }
        onClick={() => void launch()}
      >
        {state === "uploading"
          ? `Uploading ${files.length} file${files.length === 1 ? "" : "s"}...`
          : state === "launching"
            ? "Starting..."
            : discover
              ? "Send the scout"
              : "Start the run"}
      </button>
      {state === "error" && (
        <p style={{ color: "var(--critical)" }}>{message}</p>
      )}
    </div>
  );
}
